/**
 * English (US). Partial catalogs are legal: anything missing falls back to
 * es-MX, so this file can be completed incrementally without breaking a build.
 *
 * Typed against `MessageCatalog` rather than `Messages` itself: `Messages` is
 * `typeof esMX`, and es-MX is declared `as const`, so its value types are the
 * literal Spanish strings. A translation is, definitionally, a different
 * string — annotating this file as `Partial<Messages>` made every English
 * value a type error against its own Spanish literal. `MessageCatalog` is
 * the same set of keys with values widened to `string`.
 */
export const enUS = {
    'auth.title': 'Enter your PIN',
    'auth.staffCode': 'Staff code',
    'auth.pin': 'PIN',
    'auth.signIn': 'Sign in',
    'auth.wrongPin': 'That PIN does not match',
    'auth.attemptsLeft': '{count} attempts left',
    'auth.locked': 'Account locked. Try again in {minutes} minutes',
    'auth.offlineNotice': 'Offline. Verifying on this device',
    'auth.unknownStaff': 'Staff code not found',
    'auth.selectStaff': 'Select your name',
    'auth.noStaffSynced': 'This terminal has no synced staff yet. Check the server and token in Settings and wait for the first sync',
    'shift.openTitle': 'Open shift',
    'shift.openingBalance': 'Opening cash balance',
    'shift.openingBalanceHelp': 'Count the cash you are starting with and enter the amount',
    'shift.open': 'Open shift',
    'shift.closeTitle': 'Close shift',
    'shift.closingBalance': 'Cash in drawer at close',
    'shift.closingBalanceHelp': 'Count all the cash in the drawer and enter the amount',
    'shift.close': 'Close shift and sign out',
    'shift.closeConfirm': 'Closing the shift signs you out of this terminal',
    'shift.closed': 'Shift closed',
    'shift.elapsed': 'Shift open for {duration}',
    'shift.mustOpen': 'Open a shift before selling',
    'shift.invalidAmount': 'Enter a valid amount',
    'catalog.allCategories': 'All',
    'catalog.empty': 'No products in this category',
    'catalog.addProduct': 'Add {name}, {price}',
    'catalog.fromPrice': 'From {price}',
    'catalog.chooseProduct': 'Choose {name}, from {price}',
    'options.defaultLabel': 'Options',
    'options.mixHint': 'The most expensive chosen option is charged',
    'options.mixPriceHint': '{count} or more flavours: {price}',
    'options.chosen': 'Chosen: {list}',
    'options.optional': '(optional)',
    'options.extrasNeedFlavor': 'Choose at least one flavor: an extra goes on top of a flavor',
    'options.add': 'Add',
    'catalog.each': 'each',
    'cart.title': 'Sale',
    'cart.empty': 'Add products to start the sale',
    'cart.items': 'Items',
    'cart.subtotal': 'Subtotal',
    'cart.discount': 'Discount',
    'cart.comboCoffee': 'Coffee with a dish, 50 % off',
    'cart.promoFigJam': 'Free fig mermelaana, 50 g',
    'cart.total': 'Total',
    'cart.clear': 'Clear sale',
    'cart.clearConfirm': 'Every item in this sale will be removed',
    'cart.clearConfirmWithPayments': 'All items on this sale will be removed and the cash payments already recorded will be dropped',
    'cart.remove': 'Remove',
    'cart.added': '{name} added to the sale',
    'cart.qty': 'Quantity for {name}',
    'cart.decrease': 'Decrease quantity for {name}',
    'cart.increase': 'Increase quantity for {name}',
    'cart.lineDiscount': 'Discount this item',
    'cart.removeDiscount': 'Remove discount',
    'cart.orderDiscount': 'Discount the sale',
    'cart.pay': 'Charge',
    'held.hold': 'Hold sale',
    'held.open': 'Held ({count})',
    'held.title': 'Held sales',
    'held.empty': 'No held sales',
    'held.heldAt': 'Held at {time}',
    'held.resume': 'Resume',
    'held.discard': 'Delete held sale',
    'held.discardConfirm': 'This held sale and its items will be deleted. This cannot be undone',
    'held.errorTitle': 'Could not complete',
    'held.errorBlocked': 'This sale has payments recorded or a card-terminal charge in progress. Go to Charge, remove the payments or discard the charge, then try again',
    'held.errorDiscardBlocked': 'This held sale has a card payment or a terminal charge, so it cannot be deleted. Resume it to remove the payment or finish it',
    'held.discardConfirmWithPayments': 'This held sale, its items and the cash payments recorded will be deleted. This cannot be undone',
    'held.paid': 'Paid {amount}',
    'held.chargePending': 'Terminal charge pending',
    'shift.closeBlockedByHeld': 'Some held sales have payments recorded. Resume and finish them, or remove their payments, before closing the shift',
    'held.errorFailed': 'Held sales could not be saved or read. Nothing was lost; try again',
    'discount.title': 'Apply a discount',
    'discount.percent': 'Percentage',
    'discount.amount': 'Amount',
    'discount.reason': 'Reason',
    'discount.reasonRequired': 'Select the reason for the discount',
    'discount.selectReason': 'Select a reason',
    'discount.note': 'Note',
    'discount.noteRequired': 'This reason requires a note',
    'discount.overCeiling': 'This discount is over your {pct}% limit',
    'discount.adminPin': 'Authorization PIN',
    'discount.adminPinHelp': 'Ask a manager to enter their PIN',
    'discount.authorized': 'Authorized by a manager',
    'discount.resultingAmount': 'Discount to apply',
    'discount.apply': 'Apply discount',
    'discount.quick': 'Quick discounts',
    'discount.customValue': 'Custom amount',
    'discount.fromSubtotal': 'from',
    'discount.orderExcludes': 'Does not apply to what already has a discount or promotion (for example, a coffee with a dish, and that dish).',
    'discount.belowFloor': 'This discount takes the sale below the authorized minimum. Ask an administrator for their PIN.',
    'payment.title': 'Payment',
    'payment.remaining': 'Remaining',
    'payment.cash': 'Cash',
    'payment.card': 'Card',
    'payment.received': 'Received',
    'payment.change': 'Change',
    'payment.exact': 'Exact amount',
    'payment.cardType': 'Card type',
    'payment.credit': 'Credit',
    'payment.debit': 'Debit',
    'payment.authNumber': 'Authorization number',
    'payment.authNumberRequired': 'Enter the authorization number',
    'payment.addPayment': 'Add payment',
    'payment.removePayment': 'Remove payment',
    'payment.settled': 'Fully paid',
    'payment.overpaid': 'The amount is more than what remains',
    'payment.complete': 'Charge and print',
    'payment.mixedHint': 'You can combine cash and one or more cards',
    'payment.quickTenders': 'Suggested amounts',
    'payment.amount': 'Amount',
    'payment.completed': 'Sale completed',
    'payment.printedOk': 'Ticket printed',
    'payment.returningIn': 'Returning to the sale screen in {count} s',
    'kitchen.nameTitle': 'Name for the kitchen order',
    'kitchen.nameLabel': 'Customer name (optional)',
    'kitchen.namePlaceholder': 'E.g. Ana',
    'kitchen.print': 'Print kitchen order',
    'kitchen.printNoName': 'No name',
    'payment.kitchenSend': 'Send order to kitchen',
    'payment.kitchenSending': 'Printing kitchen order…',
    'payment.kitchenSent': 'Order sent to the kitchen',
    'payment.kitchenFailed': 'The kitchen order could not be printed. Check the printer and try again',
    'ticket.kitchenTitle': 'KITCHEN ORDER',
    'ticket.kitchenReprint': 'REPRINT',
    'ticket.reprintKitchenAction': 'Reprint kitchen order',
    'history.reprintKitchenSuccess': 'Kitchen order reprinted',
    'history.reprintKitchenFailed': 'Could not reprint the kitchen order',
    'payment.newSale': 'New sale',
    'payment.terminalPush.button': 'Charge with terminal',
    'payment.terminalPush.pickTitle': 'Which terminal?',
    'payment.terminalPush.resumeTitle': 'There is a pending terminal charge',
    'payment.terminalPush.resumeBody': '{amount} was sent to a terminal and the result was never confirmed. You can keep waiting for that answer or discard it and try again.',
    'payment.terminalPush.resume': 'Keep waiting',
    'payment.terminalPush.discard': 'Discard',
    'payment.terminalPush.checking': 'Checking the charge status…',
    'payment.terminalPush.discardConfirmBody': "We couldn't confirm whether the charge already went through on the terminal. Discarding it does not cancel a charge that did land — it only stops reminding you about it.",
    'payment.terminalPush.discardConfirmForce': 'Discard anyway',
    'payment.terminalPush.discardConfirmKeep': "No, don't discard",
    'payment.terminalPush.waiting': 'Waiting for the charge on the terminal…',
    'payment.terminalPush.waitingAmount': 'Ask the customer to tap or insert their card',
    'payment.terminalPush.cancel': 'Cancel',
    'payment.terminalPush.timedOut': 'The terminal is taking longer than usual. You can keep waiting or cancel.',
    'payment.terminalPush.cardTypePrompt': 'Credit or debit?',
    'payment.terminalPush.declined': 'The terminal declined the charge',
    'payment.terminalPush.cancelled': 'The charge was cancelled on the terminal',
    'payment.terminalPush.expired': 'The charge expired with no answer from the terminal',
    'payment.terminalPush.error': 'Could not confirm the charge result. The terminal may have already received it',
    'payment.terminalPush.mismatchTitle': "The charge doesn't match the current sale",
    'payment.terminalPush.mismatchBody': 'A {amount} charge was approved on {terminal} (ref. {ref}), but it does not match this sale\'s total. Ask an administrator to reconcile it manually with that reference.',
    'payment.terminalPush.mismatchDiscard': 'Discard this charge',
    'payment.terminalPush.mismatchDiscardConfirmBody': 'This {amount} charge on {terminal} (ref. {ref}) DID complete on the terminal. Discarding it here does not cancel the charge or the money collected — it only stops tracking it on this device. Ask an administrator to reconcile it manually with that reference.',
    'payment.terminalPush.retry': 'Retry',
    'payment.terminalPush.manualEntry': 'Enter manually',
    'ticket.printFailed': 'Could not print. The sale was recorded',
    'ticket.reprintAction': 'Reprint ticket',
    'ticket.noPrinter': 'No printer detected',
    'history.title': 'Past sales',
    'history.search': 'Search by folio or amount',
    'history.empty': 'No sales recorded yet',
    'history.noResults': "We couldn't find a sale matching that",
    'history.void': 'Void sale',
    'history.voidReason': 'Reason for the void',
    'history.voidReasonRequired': 'Enter the reason for the void',
    'history.voidConfirm': 'The sale will be voided. This cannot be undone',
    'history.voidedLabel': 'Voided',
    'history.synced': 'Synced',
    'history.notSynced': 'Not synced yet',
    'history.detailTitle': 'Sale detail',
    'history.reprintSuccess': 'Ticket reprinted',
    'history.reprintFailed': 'Could not reprint the ticket',
    'reports.title': 'Reports',
    'reports.today': 'Today',
    'reports.week': 'This week',
    'reports.month': 'This month',
    'reports.custom': 'Range',
    'reports.grossSales': 'Gross sales',
    'reports.netSales': 'Net sales',
    'reports.transactions': 'Transactions',
    'reports.itemsSold': 'Items sold',
    'reports.discounts': 'Discounts',
    'reports.averageTicket': 'Average ticket',
    'reports.byPaymentMethod': 'By payment method',
    'reports.topItems': 'Top items',
    'reports.noData': 'No data for this period',
    'reports.item': 'Item',
    'reports.units': 'Units',
    'reports.amount': 'Amount',
    'reports.range.from': 'From',
    'reports.range.to': 'To',
    'reports.range.apply': 'Apply',
    'sync.online': 'Online',
    'sync.offline': 'Offline',
    'sync.pending': '{count} pending',
    'sync.syncing': 'Syncing',
    'sync.dead': '{count} records need attention',
    'keypad.decimal': 'Decimal point',
    'keypad.clear': 'Clear all',
    'keypad.clearShort': 'C',
    'keypad.backspace': 'Delete last digit',
    'app.error.title': 'Something went wrong',
    'app.error.body': 'The app hit an unexpected error. Your sale in progress was not lost.',
    'app.error.reload': 'Reload',
    'settings.title': 'Settings',
    'settings.adminGateTitle': 'Settings are protected',
    'settings.adminGateHelp': 'Terminal settings are for administrators only. Enter an administrator PIN to continue.',
    'settings.adminGateSubmit': 'Verify PIN',
    'settings.terminalName': 'Terminal name',
    'settings.terminalId': 'Terminal ID',
    'settings.terminalToken': 'Pairing token',
    'settings.terminalTokenHelp': 'Provided by an admin to pair this terminal',
    'settings.terminalTokenSave': 'Save token',
    'settings.provisioningWaiting': 'Waiting for admin approval…',
    'settings.provisioningRejected': 'An admin rejected this terminal. You can enter a token manually, or ask for it to be approved again.',
    'settings.provisioningManualFallback': 'You can also enter the token manually if you prefer:',
    'settings.language': 'Language',
    'settings.kitchenPrinter': 'Kitchen order printer',
    'settings.kitchenPrinterHelp': "Sale receipts always print on this terminal's printer; only the kitchen order can go to another.",
    'settings.kitchenPrinterInternal': "This terminal's printer",
    'settings.kitchenPrinterUsb': 'USB printer',
    'settings.kitchenPrinterDevice': 'USB printer',
    'settings.kitchenPrinterAuto': 'Automatic (the first one detected)',
    'settings.kitchenPrinterNone': 'No USB printer detected',
    'settings.kitchenPrinterRefresh': 'Search again',
    'settings.kitchenPrinterWidth': 'Characters per line',
    'settings.kitchenPrinterTest': 'Print a test kitchen order',
    'settings.kitchenPrinterTestSent': 'Test kitchen order sent',
    'settings.kitchenPrinterTestFailed': 'Could not print on the kitchen order printer',
    'settings.kitchenPrinterFailNoDevice': 'Android cannot see that USB printer: check the cable and that it is on',
    'settings.kitchenPrinterFailDenied': 'The USB permission was refused: try again and tap OK',
    'settings.kitchenPrinterFailTimeout': 'No answer in time (USB permission or printer): try again',
    'settings.kitchenPrinterFailNoEndpoint': 'The printer offers no print channel the app recognises',
    'settings.kitchenPrinterFailOpen': 'Could not open the USB printer (is another app using it?)',
    'settings.kitchenPrinterFailWrite': 'The USB printer stopped answering while sending the kitchen order',
    'settings.kitchenPrinterTestLabel': 'TEST KITCHEN ORDER',
    'settings.kitchenPrinterTestAccents': 'Accents: áéíóúñ ÁÉÍÓÚÑ ¿¡',
    'settings.theme': 'Theme',
    'settings.themeHelp': 'Screen appearance on this device',
    'settings.themeDark': 'Dark',
    'settings.themeLight': 'Light',
    'settings.debugMode': 'Debug mode',
    'settings.debugModeHelp': 'Turns on detailed logging on this device',
    'settings.printerStatus': 'Printer status',
    'settings.printerReady': 'Ready',
    'settings.printerNotReady': 'Not available',
    'settings.printerTest': 'Print test ticket',
    'settings.printerTestLabel': 'Test ticket',
    'settings.printerTestSent': 'Test ticket sent',
    'settings.diagnosticsTitle': 'Diagnostics',
    'settings.diagnosticsHelp': 'These logs are sent with the next sync to help troubleshoot issues',
    'settings.syncTitle': 'Sync',
    'settings.syncAllSent': 'Everything is synced with the server',
    'settings.syncPending': '{count} operations stored on this terminal, waiting to be sent',
    'settings.deadSyncTitle': 'Sync errors',
    'settings.deadSyncEmpty': 'No record has been rejected by the server',
    'settings.deadSyncCount': '{count} records need attention',
    'settings.saved': 'Changes saved',
    // Self-update (2026-07-31)
    'settings.updateTitle': 'App update',
    'settings.updateVersion': 'Version {versionName} ({versionCode})',
    'settings.updateVersionWeb': 'Web build',
    'settings.checkUpdate': 'Check for update',
    'settings.updateUpToDate': 'You already have the latest version',
    'settings.updateAvailableRow': 'New version available: {versionName}',
    'update.title': 'Update available',
    'update.available': 'New version available: {versionName}. Update now?',
    'update.size': 'Size: {size}',
    'update.updateNow': 'Update',
    'update.downloading': 'Downloading… {percent}%',
    'update.verifying': 'Verifying…',
    'update.installing': 'Opening installer…',
    'update.done': 'Installer opened',
    'update.errorDownloadFailed': 'Could not download the update. Check your connection and try again',
    'update.errorChecksumMismatch': 'The downloaded file did not match what was published. Try again',
    'update.errorInstallPermissionRequired': 'Turn on "Install unknown apps" for this app in Settings, then try again',
    'update.errorInstallFailed': 'Could not open the installer. Try again',
    'update.errorUnknown': 'Could not finish the update. Try again',
    'update.openInstallSettings': 'Open install settings',
    'shiftBar.updateShort': 'Update',
    'shiftBar.updateAvailable': 'Update available: {versionName}. Tap to install',
    'settings.serverUrl': 'Sync server',
    'settings.serverUrlHelp': 'Full address with https://, no trailing slash. Changing it requires restarting the app',
    'nav.label': 'Main navigation',
    'nav.sale': 'Sale',
    'nav.history': 'History',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    'nav.closeShift': 'Close shift',
    'nav.switchUser': 'Switch user',
    'auth.switchUserTitle': 'Switch user',
    'auth.switchUserHint': 'The shift {name} opened and the drawer cash stay as they are. New sales are recorded under whoever signs in.',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.close': 'Close',
    'common.signOut': 'Sign out',
    'common.loading': 'Loading',
    'common.error': 'Something went wrong',
    'common.retry': 'Retry',
    'common.required': 'Required field',
    // stock & media
    'catalog.stockAvailable': '{count} available',
    'catalog.stockOut': 'Out of stock',
    'catalog.stockOversold': '{count} sold over stock',
};
//# sourceMappingURL=en-US.js.map